import {useState, useCallback, useRef,useContext, useEffect, useMemo} from 'react';
import {Route, Switch, useHistory, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import {fetchPendingOrders} from "../../services";
import {fetchWarehouseShippingMethods} from "../../services/warehouseSevices"
import {UserContext} from "../../hooks/UserContext";
import {useDispatch, useSelector} from "react-redux";
import {
    ActionMenu,
    Button, ConfirmDialog,
    CarrierName,
    FormattedDate, Notification,
    PageTopBar,
    PageView, SlidePanel,
} from "../../components"
import { BasicTable } from "./../../components/BasicTable/BasicTable"
import {SHOW_ORDERS} from "../../navigation"
import {GENERATE_LABEL, ORDERS} from "../../navigation/constants"
import { StatusPill } from '../../components/BasicTable/StatusPill'
import { ORDER_STATUSES} from '../../components/BasicTable/StatusPillDefinitions';
import { Loader } from '../../components/Loader';
import {ShowOrderContainer} from "./ShowOrderContainer";
import { DateTimeRangeFilter, SelectColumnFilter, SelectColumnMultiFilter, NumericRangeFilter, DateTimeFilter } from '../../components/BasicTable/modules/ColumnFiltersUI';
import { AssignOrdersDialog } from './AssignOrdersDialog';
import { PickabilityBadge, PICKABILITY_STATUSES } from './PickabilityBadge';
import  sameDay  from './../../sameday.json'
import { shipping_methods} from '../../components/BasicTable/ListShippingMethods';
import { setOrdersListPageSize } from "../../redux/pageSizeSlice";
import {pickAllOrderStocks} from "../../services/orderServices";
import { FormattedRelativeTime } from '../../components/FormattedRelativeTime';
import { useTranslation } from 'react-i18next';
import { formatShippingMethodFilterLabel, needsManualRemPackingTime } from '../../utils/orderUtils';
import cloneDeep from 'lodash/cloneDeep'
// Disabled along with the DEMO default below; warehouseAreaFilters.js and its specs are
// kept intact so restoring this is a one-line change once the API filter is fixed.
// import { ALWAYS_EXCLUDED_WAREHOUSE_AREAS, buildNotWarehouseArea } from './warehouseAreaFilters'
import { SetRemPackingTimeDialog } from './SetRemPackingTimeDialog'

const PAGE_SIZE_OPTIONS = [
    { id: "25", name: '25'},
    { id: "50", name: '50' },
    { id: "100", name: '100' },
    { id: "200", name: '200' }
]

export const ListOrderContainer = () => {
    const history = useHistory()
    const {user} = useContext(UserContext)
    const {warehouse_id} = useParams()
    const dispatch = useDispatch()
    const pageSize = useSelector((state) => state.pageSize.ordersList)
    const {i18n} = useTranslation()

    const defaultSearchParams = {
        page: 1,
        per_page: pageSize,
        status: ['pending'],
        order_by: 'max_packing_time',
        order: 'ASC',
        warehouse_id: isNaN(parseInt(warehouse_id)) ? null : parseInt(warehouse_id),
        pick_n_ship: false,
        // TEMPORARILY DISABLED — sending not_warehouse_area on every request is what
        // makes this list time out with a 500 in Sao Paulo - Embu. The API filters it
        // against a derived column, which defers the area aggregation until after the
        // store join and drives the page from ~0.05s to ~15s, past the 25s request
        // budget once the warehouse's pending queue is large. Restore this line (and the
        // two below) once the API filters areas in the inner subquery instead.
        // not_warehouse_area: [...ALWAYS_EXCLUDED_WAREHOUSE_AREAS],
    }

    const [showBulkPickSuccess, setShowBulkPickSuccess] = useState(false)
    const [showBulkPickConfirmation, setShowBulkPickConfirmation] = useState(false)
    const [selectedBulkPickOrder, setSelectedBulkPickOrder] = useState(null)
    const [loadingBulkPick, setLoadingBulkPick] = useState(null)
    const [searchParams, setSearchParams] = useState({...defaultSearchParams})
    const [selectedOrders, setSelectedOrders] = useState([])
    const [remEtaOrder, setRemEtaOrder] = useState(null)

    const additionalParams = {
        include_inactive: true
    }

    const {
        data: shippingMethodsData,
        isLoading: isLoadingShippingMethods ,
        isError: isErrorShippingMethods,
        error: errorShippingMethods,
        isFetching: isFetchingShippingMethods,
        isPreviousData: isPreviousDataShippingMethods,
        refetch: refetchShippingMethods
    } = useQuery(['shipping_methods', warehouse_id], () => fetchWarehouseShippingMethods(warehouse_id, additionalParams), {
        keepPreviousData : true,
        refetchOnWindowFocus: false
    })

    const isChileWarehouse = user?.current_warehouse?.country === 'CL'

    const table_columns = [
        {
            Header: i18n.t('list_orders.headers.store'),
            accessor: 'store',
            disableFilters: false,
            disableSortBy: false,
            placeholder: i18n.t('list_orders.store_search_placeholder'),
            inputHoverDescription: (
                <div>
                    {i18n.t('list_orders.headers.store_search_help_1')}
                    <br/> <br/>
                    {i18n.t('list_orders.headers.store_search_help_2')}
                </div>
            )

        },
        {
            Header: i18n.t('list_orders.headers.area'),
            accessor: 'warehouse_area', // accessor is the "key" in the data
            disableFilters: false,
            disableSortBy: false,
            placeholder: i18n.t('list_orders.area_search_placeholder'),
            inputHoverDescription: (
                <div>
                    {i18n.t('list_orders.headers.area_search_help_1')}
                    <br/> <br/>
                    {i18n.t('list_orders.headers.area_search_help_2')}
                </div>
            )
        },
        {
            Header: i18n.t('list_orders.headers.products'),
            accessor: 'assigned_stock_quantity',
            Filter: NumericRangeFilter,
            disableFilters: false,
            disableSortBy: false,
            shrinkToContent: true
        },
        {
            Header: i18n.t('list_orders.headers.max_packing_time'),
            accessor: 'max_packing_time',
            Filter: DateTimeFilter,
            disableFilters: false,
            disableSortBy: false,
            shrinkToContent: true
        },
        {
            Header: i18n.t('list_orders.headers.order_number'),
            accessor: 'order_number', // accessor is the "key" in the data
            disableFilters: false,
            disableSortBy: false,
            placeholder: i18n.t('list_orders.order_number_search_placeholder'),
            inputHoverDescription: (
                <div>
                    {i18n.t('list_orders.headers.order_number_search_help')}
                </div>
            )
        },
        ...(isChileWarehouse ? [
            {
                Header: i18n.t('list_orders.headers.city'),
                accessor: 'city',
                disableFilters: false,
                disableSortBy: true,
                placeholder: i18n.t('list_orders.city_search_placeholder'),
                inputHoverDescription: (
                    <div>
                        {i18n.t('list_orders.headers.city_search_help')}
                    </div>
                )
            },
            {
                Header: i18n.t('list_orders.headers.state'),
                accessor: 'state',
                disableFilters: false,
                disableSortBy: true,
                placeholder: i18n.t('list_orders.state_search_placeholder'),
                inputHoverDescription: (
                    <div>
                        {i18n.t('list_orders.headers.state_search_help_1')}
                        <br/> <br/>
                        {i18n.t('list_orders.headers.state_search_help_2')}
                    </div>
                )
            },
        ] : []),
        {
            Header: i18n.t('list_orders.headers.channel_name'),
            accessor: 'channel_name',
            disableFilters: false,
            disableSortBy: true,
            Filter: SelectColumnFilter,
            selectFilterOptions: ['manual', 'shopee', 'meli', 'liverpool', 'walmart', 'vtex'].map((channel_type)=>{

                return {
                    id: channel_type,
                    label: channel_type
                }
            }),
        },
        {
            Header: i18n.t('list_orders.headers.shipping_type'),
            accessor: 'shippment_type',
            Filter: SelectColumnFilter,
            selectFilterOptions: ['sameday', 'standard'].map((shipping_type)=>{

                return {
                    id: shipping_type,
                    label: shipping_type === 'sameday' ? 'Mismo día' : 'Estándar'
                }
            }),
            disableFilters: false,
            disableSortBy: true,
        },
        /*{
            Header: 'En pendiente desde',
            accessor: 'pending_at', // accessor is the "key" in the data
            Filter: DateTimeRangeFilter,
            disableFilters: false,
            disableSortBy: false,
            shrinkToContent: true
        },*/
        ...(user?.permissions?.beta_features ? [{
            Header: i18n.t('list_orders.headers.pickability'),
            accessor: 'pickability_status',
            disableFilters: false,
            disableSortBy: true,
            shrinkToContent: true,
            Filter: SelectColumnMultiFilter,
            selectFilterOptions: PICKABILITY_STATUSES.map((s) => ({
                id: s.status,
                label: i18n.t(`pickability.status.${s.status}`),
            })),
        }] : []),
        {
            Header: "",
            accessor: 'actions', // accessor is the "key" in the data
            disableFilters: true,
            disableSortBy: true,
            fetchingIndicator : true,
            shrinkToContent: true
        },
    ]


    const {
        isLoading,
        isError,
        error,
        data,
        isFetching,
        isPreviousData,
        refetch,
    } = useQuery(['pending_orders', searchParams], () => fetchPendingOrders(searchParams), { keepPreviousData : true })

    const handlerOnClickStatus = (order) => {
        // setOrderClicked(order)
        // setOpenDialogErrors(!openDialogErrors)
        history.push(SHOW_ORDERS.replace(':id', order.id).replace(':warehouse_id', warehouse_id))
    }


    const onSortChange = (orderBy) => {
        // console.log("ON_ORDER_BY",orderBy)
        tableRef.current?.resetSelection()
        tableRef.current?.resetPagination()
        if (orderBy.length > 0) {
            setSearchParams({
                ...searchParams,
                order_by: orderBy[0].id,
                order: orderBy[0].desc ? 'desc' : 'asc'
            })
        }
        else {
            if ('order_by' in searchParams) {
                searchParams.order_by = defaultSearchParams.order_by
                searchParams.order = defaultSearchParams.order
                setSearchParams({...searchParams})
            }
        }
    }

    const onFilterChange = (activeFilters) => {
        // Create a new object without the column accessor properties
        let newSearchParams = cloneDeep(searchParams)
        memoizedColumns.forEach(col => {
            if (newSearchParams[col.accessor]) {
                delete newSearchParams[col.accessor]
            }
        })
        // The shipping_method column sends shipping_method_id, which no accessor
        // matches, so the loop above would leave a cleared filter behind.
        delete newSearchParams.shipping_method_id

        let filters = {}
        activeFilters.forEach(filter => {
            let value = filter.value
            if (filter.id === 'store') {
                let splitValues
                if (value.includes(',')) {
                    splitValues = value.split(',').map(item => item.trim()).filter(item => item !== '')
                } else {
                    splitValues = value.split(/\s+/).filter(item => item !== '')
                }
                let notStoreValues = splitValues.filter(v => v.startsWith('-')).map(v => v.slice(1))
                let storeValues = splitValues.filter(v => !v.startsWith('-'))
                value = storeValues
                filters['not_store_values'] = notStoreValues
            }
            else if (filter.id === 'warehouse_area') {
                let splitValues
                if (value.includes(',')) {
                    splitValues = value.split(',').map(item => item.trim()).filter(item => item !== '')
                } else {
                    splitValues = value.split(/\s+/).filter(item => item !== '')
                }
                let notWarehouseArea = splitValues.filter(v => v.startsWith('-')).map(v => v.slice(1))
                let warehouseArea = splitValues.filter(v => !v.startsWith('-'))
                value = warehouseArea
                // Only what the user typed as `-XYZ`. The DEMO default is disabled — see
                // defaultSearchParams above. Excluding an area on demand still works, and
                // is slow only for whoever asks for it, instead of on every page load.
                // filters['not_warehouse_area'] = buildNotWarehouseArea(notWarehouseArea, warehouseArea)
                filters['not_warehouse_area'] = notWarehouseArea
            }
            else if (filter.id === 'state') {
                let splitValues = []
                if (typeof value === 'string') {
                    splitValues = value.includes(',')
                        ? value.split(',').map(item => item.trim()).filter(item => item !== '')
                        : (value.trim() !== '' ? [value.trim()] : [])
                } else if (Array.isArray(value)) {
                    splitValues = value
                }
                const notStateValues = splitValues
                    .filter(v => typeof v === 'string' && v.startsWith('-'))
                    .map(v => v.slice(1).trim())
                    .filter(v => v !== '')
                const stateValues = splitValues.filter(v => typeof v !== 'string' || !v.startsWith('-'))
                value = stateValues.length === 1 ? stateValues[0] : stateValues
                filters['not_state'] = notStateValues
            }
            else if (filter.id === 'city' || filter.id === 'order_number') {
                if (typeof value === 'string' && value.includes(',')) {
                    let splitValues = value.split(',').map(item => item.trim()).filter(item => item !== '')
                    value = splitValues.length === 1 ? splitValues[0] : splitValues
                }
            }
            // Send the shipping method as an exact id rather than as its name. The
            // name filter is a fuzzy match on "carrier + shipping name", so picking
            // "LOGGI - Loggi" also pulled in "LOGGI - Loggi Extrema".
            else if (filter.id === 'shipping_method') {
                filters['shipping_method_id'] = value
                return
            }

            filters[filter.id] = value
        })

        // Handle not_store_values and not_state removal immutably
        if (!('not_store_values' in filters) || filters.not_store_values.length === 0) {
            const {not_store_values, ...restParams} = newSearchParams
            newSearchParams = restParams
        }
        // Dropped like the other exclusions again, so clearing the ÁREA filter stops
        // sending the param. The DEMO fallback that used to live here is disabled:
        // if (!('not_warehouse_area' in filters)) {
        //     filters.not_warehouse_area = [...ALWAYS_EXCLUDED_WAREHOUSE_AREAS]
        // }
        if (!('not_warehouse_area' in filters) || filters.not_warehouse_area.length === 0) {
            const {not_warehouse_area, ...restParams} = newSearchParams
            newSearchParams = restParams
        }
        if (!('not_state' in filters) || filters.not_state.length === 0) {
            const {not_state, ...restParams} = newSearchParams
            newSearchParams = restParams
        }

        if (!('status' in filters) || filters.status.length === 0) {
            filters.status = [...defaultSearchParams.status]
        }

        tableRef.current?.resetSelection()
        tableRef.current?.resetPagination()
        setSearchParams({...newSearchParams, ...filters, page: 1})
    }

    const getTotalStockUnitCount = (orderLines) => {
        let count = 0
        orderLines.forEach(ol => {
            count += ol.quantity
        })
        return count
    }

    const sameDayOrder = (code = "", carrier = "") => {
        let newJson = sameDay["codes"]

        let foundIndex = newJson.findIndex(def => def == code);

        if(foundIndex != -1 && carrier === 'TREGGO')  {
            return "Mismo día";
        }   else    {
            return "Estándar";
        }
    }

    const handleBulkPick = function(order) {
        setSelectedBulkPickOrder(order)
        setShowBulkPickConfirmation(true)
    }

    const cleanBulkPickState = function() {
        setSelectedBulkPickOrder(null)
        setShowBulkPickConfirmation(false)
    }

    const onConfirmBulkPick = async function(order) {
        setLoadingBulkPick(true)

        try {
            await pickAllOrderStocks(order.id)
        } catch (error) {
            window.alert(error.response.data?.error)
            console.log(error)
        }

        await refetch()
        cleanBulkPickState()
        setShowBulkPickSuccess(true)
        setLoadingBulkPick(false)
    }

    const buildActions = function (order) {
        const actions = [
            {
                title: i18n.t('list_orders.actions.see_order'),
                clickHandler: ()=> history.push(SHOW_ORDERS.replace(':id', order.id).replace(':warehouse_id', warehouse_id))
            },
            {
                title: i18n.t('list_orders.actions.generate_shipment'),
                clickHandler: ()=> history.push(GENERATE_LABEL.replace(':id', order.id).replace(':warehouse_id', warehouse_id))
            }
        ]

        if (user?.permissions?.pick_all_order_stocks && order.assigned_stock_quantity && order.assigned_stock_quantity >= 20) {
            actions.push({
                title: i18n.t('list_orders.actions.pick_pieces'),
                clickHandler: ()=> handleBulkPick(order)
            })
        }

        return actions
    }

    const buildWarehouseArea = order_lines =>
        [...new Set(order_lines.map(order_line => order_line.stock_units.map(stock_unit => stock_unit.warehouse_location?.area )).flat())].join(', ')


    const getTableData = () => {
        return data.orders.map((order)=>{
            if(order.store.name.length > 27) order.store.name = order.store.name.substring(0, 24) + '...'
            return {
                entity: {...order},
                store:<div className="">{String(order.store.name)}</div>,
                order_number: (
                    <div className="flex items-center gap-1">
                        <span className="hover:underline cursor-pointer" onClick={()=>handlerOnClickStatus(order)}>#{String(order.order_number)}</span>
                        {user?.permissions?.beta_features && order.is_b2b && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">{i18n.t('picking_tasks.badge_b2b')}</span>}
                    </div>
                ),
                channel_name: order.channel_name,
                pickability_status: <PickabilityBadge pickability={order.pickability} orderNumber={order.order_number} warehouseId={warehouse_id} />,
                city: order.shipping?.city || '',
                state: order.shipping?.state || '',
                warehouse_area: order.warehouse_areas,
                shipping_method: formatShippingMethodFilterLabel(order.shipping_method),
                pending_at: <FormattedDate date={order.pending_at} shortDate />,
                max_packing_time: order.max_packing_time
                    ? <FormattedRelativeTime date={order.max_packing_time} calendarTime/>
                    : (needsManualRemPackingTime(order)
                        ? <div className="flex items-center gap-1">
                            <span className="text-gray-500 italic">{i18n.t('list_orders.rem_packing_time.to_be_agreed')}</span>
                            {user?.permissions?.wms_set_rem_packing_time &&
                                <button
                                    type="button"
                                    className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                                    onClick={() => setRemEtaOrder(order)}
                                >
                                    {i18n.t('list_orders.rem_packing_time.set')}
                                </button>}
                          </div>
                        : null),
                assigned_stock_quantity: order.assigned_stock_quantity,
                shippment_type: order.is_same_day_delivery ? 'Mismo día' : 'Estándar',
                actions: (
                    <ActionMenu className="float-right" items={buildActions(order)}/>
                )
            }
        })
    }

    const getColumns = () => {
        if(shippingMethodsData) {
            let newColumns = [...table_columns]
            let index = newColumns.findIndex(column => column.accessor === "shipping_method")
            if(index === -1) {
                newColumns.splice(5,0,{
                    Header: i18n.t('list_orders.headers.shipping_method'),
                    accessor: 'shipping_method',
                    disableFilters: false,
                    disableSortBy: false,
                    // Keep the string accessor for filtering/sorting; render the
                    // carrier with its internal-carrier tooltip from the original
                    // order object (CUBBO-2185).
                    Cell: ({ row }) => {
                        const sm = row.original.entity?.shipping_method
                        if (!sm) return '-'
                        return <CarrierName carrierName={sm.carrier_name} internalCarrierName={sm.internal_carrier_name} suffix={sm.shipping_name} />
                    },
                    Filter: SelectColumnFilter,
                    selectFilterOptions:shippingMethodsData.map((ship)=> {
                        return {
                            id: ship.id,
                            label: formatShippingMethodFilterLabel(ship)
                        }
                    })
                })
            }
            return newColumns
        }
        return []
    }

    const memoizedColumns = useMemo(() => getColumns(), [shippingMethodsData, isChileWarehouse, user])

    const [showAssignOrderDialog, setShowAssignOrderDialog] = useState(false)
    const onAssignOrders = () => {
        setShowAssignOrderDialog(true)
    }

    const onOrdersAssigned = () => {
        setShowAssignOrderDialog(false)
        // Keep the column filters: the table still shows what the user typed, so
        // resetting the params left the inputs filled over an unfiltered list.
        setSearchParams(prev => ({...prev, page: 1}))
        refetch()
        tableRef.current?.resetSelection()
        tableRef.current?.resetPagination()
    }

    const isBulkPickOrder = assigned_stock_quantity => assigned_stock_quantity > 1000

    const tableRef = useRef()

    return (
        <>
           <PageView
                topMenu={<PageTopBar>
                    <div className="text-lg font-semibold">{i18n.t('list_orders.pending_orders')}</div>
                    {user?.permissions?.reassign_picking_order &&
                        <Button className="whitespace-nowrap mr-2" onClick={onAssignOrders} disabled={selectedOrders.length < 1}>
                            {`${i18n.t('list_orders.assign_orders')} (${selectedOrders.length})`}
                        </Button>
                    }

                </PageTopBar>}
                childrenFullWidth={true}
                topMenuFullWidth={true}
                //bottomMenu={<Button>Crear Orden</Button>}
            >

                {(!isPreviousData && isLoading) || (!isPreviousDataShippingMethods && isLoadingShippingMethods) ? (
                    <><Loader show={true}></Loader></>
                ) : isError ? (
                    <>Error: {error.message}</>
                ) : isErrorShippingMethods ? (
                    <>Error: {errorShippingMethods.message}</>
                ):(

                        <>
                            <BasicTable
                                showHeader
                                showLoader={isFetching && isPreviousData}
                                columns={memoizedColumns}
                                filterable
                                sortable
                                showPaginator
                                selectable
                                data = {getTableData()}
                                onFilterChange={(activeFilters) => onFilterChange(activeFilters)}
                                onSortChange={(activeSort) => onSortChange(activeSort)}
                                paginationMeta={data?.meta}
                                labelToPaginator={i18n.t('common.orders')}
                                pagesSize = {searchParams.per_page}
                                pageSizeOptions={PAGE_SIZE_OPTIONS}
                                onPaginationChange= { (requestedPage) => { setSearchParams({...searchParams, page: requestedPage}) } }
                                onPageSizeChange= { (pageSize) => {
                                    setSearchParams({...searchParams, per_page: pageSize, page: 1})
                                    dispatch(setOrdersListPageSize(pageSize))
                                    tableRef.current?.resetPagination()
                                }}
                                onSelectionChange={(rows) => {
                                    setSelectedOrders(rows)
                                    // console.log("rows", rows)
                                }}
                                showDataStatus
                                isFetching={isFetching}
                                emptyTableText={i18n.t('list_orders.empty_table_text')}
                                ref={tableRef}
                            />

                        </>


                    )
                }
                {user?.permissions?.reassign_picking_order &&
                    <AssignOrdersDialog open={showAssignOrderDialog} orders={selectedOrders} warehouseId={warehouse_id} onCancel={()=>setShowAssignOrderDialog(false)} onSuccess={() => onOrdersAssigned()}></AssignOrdersDialog>
                }
            </PageView>
            <ConfirmDialog
                open={showBulkPickConfirmation}
                setOpen={setShowBulkPickConfirmation}
                title={`Confirma pickear piezas de #${selectedBulkPickOrder?.order_number}`}
                description={`Confirma que deseas pickear todas las ${selectedBulkPickOrder?.assigned_stock_quantity} piezas de este pedido.`}
                confirmLabel="Confirmar"
                cancelLabel="Cancelar"
                onConfirm={() => onConfirmBulkPick(selectedBulkPickOrder)}
                onCancel={() => cleanBulkPickState()}
                loading={loadingBulkPick}
            />
            <Notification
                show={showBulkPickSuccess}
                setShow={setShowBulkPickSuccess}
                type="success"
                title="Orden pickeada correctamente"
            />
            {remEtaOrder && (
                <SetRemPackingTimeDialog
                    order={remEtaOrder}
                    open={!!remEtaOrder}
                    onClose={() => setRemEtaOrder(null)}
                    onSaved={() => refetch()}
                />
            )}
            <Switch>
                <Route exact path={SHOW_ORDERS}>
                    <SlidePanel title="Ver Orden" referrer={ORDERS.replace(':warehouse_id', warehouse_id)}>
                        <ShowOrderContainer />
                    </SlidePanel>
                </Route>
            </Switch>
        </>
    )
}
